import {
  BrewAmount,
  BrewTemp,
  BrewTime,
  PrepMilk,
  PrepWater,
} from './BuilderUiIcons';
import './BrewingInstructionsSection.css';

function BrewSpecItem({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="brew-spec-item" role="listitem">
      <span className="brew-spec-item__icon" aria-hidden="true">{icon}</span>
      <span className="brew-spec-item__text">
        <span className="brew-spec-item__label">{label}</span>
        <span className="brew-spec-item__value">{value}</span>
      </span>
    </div>
  );
}

function BrewSpecStack({ instructions }) {
  if (!instructions) return null;

  const hasStructured =
    instructions.dose || instructions.water || instructions.milk;
  const timeLabel =
    instructions.method === 'decoction' || instructions.method === 'chai'
      ? 'Simmer Time'
      : 'Steep Time';
  const isMilkOnly = !instructions.milk && instructions.preparation === 'milk';
  const primaryVolumeLabel = isMilkOnly ? 'Milk' : 'Water';

  return (
    <div className="brew-spec-grid" role="list">
      <BrewSpecItem
        icon={<BrewTemp />}
        label="Temperature"
        value={instructions.temperature}
      />
      <BrewSpecItem
        icon={<BrewTime />}
        label={timeLabel}
        value={instructions.time}
      />
      {hasStructured ? (
        <>
          {/* Water + milk share a row when both are present */}
          <BrewSpecItem
            icon={<PrepWater />}
            label={primaryVolumeLabel}
            value={instructions.water}
          />
          <BrewSpecItem
            icon={<PrepMilk />}
            label="Milk"
            value={instructions.milk}
          />
          <BrewSpecItem
            icon={<BrewAmount />}
            label="Per Serving"
            value={instructions.dose}
          />
        </>
      ) : (
        <BrewSpecItem
          icon={<BrewAmount />}
          label="Per Serving"
          value={instructions.amount}
        />
      )}
    </div>
  );
}

function BrewSteps({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <ol className="brew-steps">
      {steps.map((step, index) => (
        <li key={index}>{step}</li>
      ))}
    </ol>
  );
}

/**
 * Recipe-page ritual: parameters (sidebar) + preparation steps side by side.
 */
function BrewingInstructionsSection({ brewingInstructions }) {
  if (!brewingInstructions) return null;

  const isDual =
    brewingInstructions.preparation_mode === 'both'
    && Array.isArray(brewingInstructions.options)
    && brewingInstructions.options.length > 0;

  const panels = isDual
    ? brewingInstructions.options
    : [brewingInstructions];

  return (
    <div className="blend-ritual">
      <header className="blend-ritual__header">
        <p className="blend-ritual__eyebrow">The Ritual</p>
        <h2 className="blend-ritual__heading">How to brew it.</h2>
      </header>

      <div className={isDual ? 'blend-ritual__dual' : undefined}>
        {panels.map((option) => {
          const key = option.preparation || option.label || 'default';
          const showSteps = option.steps && option.steps.length > 0;
          return (
            <div key={key} className="blend-ritual__option">
              {isDual && option.label && (
                <p className="blend-ritual__option-title">{option.label}</p>
              )}
              <div
                className={
                  `blend-ritual__grid`
                  + (showSteps ? '' : ' blend-ritual__grid--spec-only')
                }
              >
                <aside className="blend-ritual__spec" aria-label="Brewing parameters">
                  <BrewSpecStack instructions={option} />
                </aside>
                {showSteps && (
                  <div className="blend-ritual__steps">
                    <p className="blend-ritual__steps-label">Preparation</p>
                    <BrewSteps steps={option.steps} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BrewingInstructionsSection;
